import { useMemo } from 'react';
import { useUsers } from 'components/dataProviders/withUsers';
import { usersWithAccessSelector } from 'components/dataProviders/withUsers/model';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { ProcessType } from 'shared/domain/process/types/model';
import { FieldSkeleton } from 'shared/types/form';
import { UserRole } from 'shared/types/userRole';
import { useAllCompanies } from 'store/company';
import { useContracts } from 'store/contract/hooks';
import { toFieldValuesOfProcess, useFieldValueDictionaries } from 'store/fieldValue';
import { useIssueForm } from 'store/issueForm';
import { useLevelsToSelect } from 'store/level';
import { useSites } from 'store/site';
import { generateForm, toFormFieldValues } from 'views/issue/issueView/UnifiedIssueViewContainer/generateForm';

/** The fields of the issue's form, with the options of its process. */
export function useIssueFields({
  process,
  siteId,
  organizationId,
}: {
  process: ProcessType | undefined;
  siteId: string;
  organizationId: string;
}): {
  primaryFields: FieldSkeleton[];
  extendedFields: FieldSkeleton[];
  fieldsLoading: boolean;
} {
  const processId = process?._id;
  const { items: sites } = useSites();
  const { items: companies } = useAllCompanies();
  const { items: contracts, loading: loadingContracts } = useContracts();
  const { levels } = useLevelsToSelect();
  const { dictionaries } = useFieldValueDictionaries();
  const {
    all: { items: users },
  } = useUsers();
  const { issueForm } = useIssueForm();

  const issueManagers = useMemo(
    () =>
      usersWithAccessSelector(
        [
          UserRole.organizationAdmin,
          UserRole.projectAdmin,
          UserRole.manager,
          UserRole.viewer,
          UserRole.standard,
        ],
        siteId,
        processId || '',
        users,
        organizationId
      ),
    [siteId, processId, users, organizationId]
  );

  const fieldValuesOfProcess = useMemo(
    () => toFormFieldValues(toFieldValuesOfProcess(dictionaries, processId)),
    [dictionaries, processId]
  );

  const { primaryFields, extendedFields } = useMemo(() => {
    if (!process) {
      return { primaryFields: [], extendedFields: [] };
    }
    return generateForm({
      issueForm,
      ...fieldValuesOfProcess,
      levels: levels.filter(keepUndeleted),
      sites: sites.filter(keepUndeleted),
      companies: companies.filter(keepUndeleted),
      contracts: loadingContracts ? undefined : contracts.filter(keepUndeleted),
      usersWithAccess: issueManagers,
    });
  }, [
    process,
    issueForm,
    fieldValuesOfProcess,
    levels,
    sites,
    companies,
    contracts,
    loadingContracts,
    issueManagers,
  ]);

  return { primaryFields, extendedFields, fieldsLoading: loadingContracts };
}
